import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/UI/Button';
import Input from '../components/UI/Input';
import Card from '../components/UI/Card';

const STORAGE_ATTEMPTS = 'reg_attempts';
const STORAGE_COOLDOWN_UNTIL = 'reg_cooldown_until';

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [success, setSuccess] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [attempts, setAttempts] = useState(() => {
    const saved = localStorage.getItem(STORAGE_ATTEMPTS);
    return saved ? parseInt(saved, 10) : 0;
  });
  const [cooldownRemaining, setCooldownRemaining] = useState(() => {
    const until = localStorage.getItem(STORAGE_COOLDOWN_UNTIL);
    if (until) {
      const remaining = Math.max(0, Math.floor((parseInt(until, 10) - Date.now()) / 1000));
      return remaining;
    }
    return 0;
  });

  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem(STORAGE_ATTEMPTS, attempts.toString());
  }, [attempts]);

  useEffect(() => {
    if (cooldownRemaining > 0) {
      const expiry = Date.now() + cooldownRemaining * 1000;
      localStorage.setItem(STORAGE_COOLDOWN_UNTIL, expiry.toString());
    } else {
      localStorage.removeItem(STORAGE_COOLDOWN_UNTIL);
    }
  }, [cooldownRemaining]);

  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const timer = setTimeout(() => setCooldownRemaining(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldownRemaining]);

  useEffect(() => {
    if (cooldownRemaining === 0 && attempts >= 3) {
      setAttempts(0);
      setGeneralError('');
      localStorage.removeItem(STORAGE_ATTEMPTS);
    }
  }, [cooldownRemaining, attempts]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && success) {
      navigate('/');
    }
  }, [countdown, success, navigate]);

  const validateForm = () => {
    const newErrors = {};
    if (!email) newErrors.email = 'Email обовʼязковий';
    if (!email.includes('@')) newErrors.email = 'Невірний формат email';
    if (!password) newErrors.password = 'Пароль обовʼязковий';
    if (password.length < 6) newErrors.password = 'Пароль має містити щонайменше 6 символів';
    if (password !== confirmPassword) newErrors.confirmPassword = 'Паролі не співпадають';
    if (!firstName) newErrors.firstName = 'Імʼя обовʼязкове';
    if (!lastName) newErrors.lastName = 'Прізвище обовʼязкове';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();

    setGeneralError('');
    if (!validateForm()) return;
    if (cooldownRemaining > 0) return;

    setLoading(true);
    const result = await register(email, password, firstName, lastName);
    setLoading(false);

    if (result.success) {
      setAttempts(0);
      setCooldownRemaining(0);
      localStorage.removeItem(STORAGE_ATTEMPTS);
      localStorage.removeItem(STORAGE_COOLDOWN_UNTIL);
      setSuccess(true);
      setPendingVerification(result.pendingVerification || false);
      setSuccessMessage(
        result.pendingVerification
          ? 'Користувач з таким email вже існує, але не підтверджений. Ми надіслали лист повторно. Перевірте пошту.'
          : 'Реєстрація успішна! Перевірте вашу пошту для підтвердження.'
      );
      setCountdown(60);
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setGeneralError(result.message || 'Не вдалося зареєструватися. Спробуйте пізніше.');
      if (newAttempts >= 3) {
        setCooldownRemaining(60);
        setGeneralError('Забагато спроб. Зачекайте 60 секунд.');
      }
    }
  };

  const isButtonDisabled = loading || cooldownRemaining > 0;
  const buttonText = loading
    ? 'Реєстрація...'
    : cooldownRemaining > 0
      ? `Зачекайте ${cooldownRemaining}с`
      : 'Зареєструватися';

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full">
          <div className="mb-4">
            <Link to="/" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              На головну
            </Link>
          </div>
          <Card>
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {pendingVerification ? 'Перевірте пошту' : 'Реєстрація успішна!'}
              </h2>
              <p className="text-gray-600 mb-4">{successMessage}</p>
              <div className="bg-blue-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  Перенаправлення на головну через <strong>{countdown}</strong> секунд.
                </p>
              </div>
              <Link to="/">
                <Button variant="primary">На головну зараз</Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="mb-4">
          <Link to="/" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            На головну
          </Link>
        </div>
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <span className="font-semibold text-gray-900 text-2xl">Судовий Хаб</span>
          </Link>
        </div>
        <Card>
          <div className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Реєстрація</h2>
              <p className="text-gray-600 mt-2">Створіть обліковий запис</p>
            </div>
            {generalError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                {generalError}
              </div>
            )}
            <div>
              <Input
                label="Email"
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={errors.email}
                required
                placeholder="your@email.com"
                disabled={cooldownRemaining > 0}
              />
              <Input
                label="Пароль"
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={errors.password}
                required
                placeholder="Мінімум 6 символів"
                disabled={cooldownRemaining > 0}
              />
              <Input
                label="Підтвердіть пароль"
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={errors.confirmPassword}
                required
                placeholder="Повторіть пароль"
                disabled={cooldownRemaining > 0}
              />
              <Input
                label="Ім'я"
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                error={errors.firstName}
                required
                placeholder="Ваше ім'я"
                disabled={cooldownRemaining > 0}
              />
              <Input
                label="Прізвище"
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                error={errors.lastName}
                required
                placeholder="Ваше прізвище"
                disabled={cooldownRemaining > 0}
              />
              <div className="bg-blue-50 rounded-lg p-3 mb-4 text-sm text-blue-800">
                <p>Всі поля обов'язкові. Після реєстрації ви отримаєте лист для підтвердження.</p>
              </div>
              {cooldownRemaining > 0 && (
                <div className="bg-yellow-50 rounded-lg p-3 mb-4 text-sm text-yellow-800">
                  ⏱ Зачекайте {cooldownRemaining} секунд перед наступною спробою.
                </div>
              )}
              <Button
                onClick={handleRegister}
                variant="primary"
                size="lg"
                className="w-full mt-2"
                disabled={isButtonDisabled}
              >
                {buttonText}
              </Button>
            </div>
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Вже є акаунт?{' '}
                <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                  Увійти
                </Link>
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Register;